if (!customElements.get('cart-discount')) {
  class CartDiscount extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.js-discount-input');
      this.submitBtn = this.querySelector('.js-discount-submit');
      this.errorEl = this.querySelector('.js-discount-error');
      this.sectionContainer = this.closest('[data-section]');
      // Discount pills can be rendered outside of <cart-discount> (eg. alongside other cart
      // discounts), so listen for removal clicks on the whole section rather than just this element
      this.scope = this.sectionContainer || this;

      this.submitBtn.addEventListener('click', this.applyDiscount.bind(this));
      this.input.addEventListener('keydown', this.handleKeydown.bind(this));
      this.scope.addEventListener('click', this.handleClick.bind(this));
    }

    /**
     * Handles a 'keydown' event on the discount code input
     * @param {object} evt - Event object
     */
    handleKeydown(evt) {
      if (evt.key !== 'Enter') return;

      evt.preventDefault();
      this.applyDiscount();
    }

    /**
     * Applies the entered discount code to the cart
     */
    applyDiscount() {
      const code = this.input.value.trim();
      const appliedCodes = this.getAppliedCodes();

      if (!code || appliedCodes.includes(code)) return;

      this.updateDiscounts([...appliedCodes, code], code);
    }

    /**
     * Handles a 'click' event, used to detect clicks on a discount pill's remove button
     * @param {object} evt - Event object
     */
    handleClick(evt) {
      const removeBtn = evt.target.closest('.js-discount-remove');
      if (!removeBtn) return;

      evt.preventDefault();

      const code = removeBtn.closest('.js-discount-pill').dataset.discountCode;
      this.updateDiscounts(this.getAppliedCodes().filter((appliedCode) => appliedCode !== code));
    }

    /**
     * Gets the discount codes currently applied to the cart, from the rendered pills
     * @returns {Array}
     */
    getAppliedCodes() {
      return Array.from(this.scope.querySelectorAll('.js-discount-pill')).map((pill) => pill.dataset.discountCode);
    }

    /**
     * Updates the cart's discount codes and re-renders the cart summary
     * @param {Array} codes - Discount codes to apply
     * @param {string} [newCode] - The newly-added code, if any, used for error detection
     */
    async updateDiscounts(codes, newCode) {
      if (!this.sectionContainer) return;

      this.scope.classList.add('pointer-events-none');
      this.errorEl.hidden = true;

      try {
        const response = await fetch(theme.routes.cartUpdate, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            discount: codes.join(','),
            sections: [this.sectionContainer.dataset.section]
          })
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.errors || response.status);

        if (newCode) {
          const invalidCode = (data.discount_codes || []).find(
            (discount) => discount.code === newCode && discount.applicable === false
          );

          if (invalidCode) {
            this.input.value = '';
            this.errorEl.hidden = false;
            return;
          }
        }

        const tmpl = document.createElement('template');
        tmpl.innerHTML = data.sections[this.sectionContainer.dataset.section];

        const newSectionContainer = tmpl.content.querySelector(`#${this.sectionContainer.id}`);
        if (newSectionContainer) this.sectionContainer.innerHTML = newSectionContainer.innerHTML;
      } catch (error) {
        this.errorEl.hidden = false;
        console.log(error); // eslint-disable-line
      } finally {
        this.scope.classList.remove('pointer-events-none');
      }
    }
  }

  customElements.define('cart-discount', CartDiscount);
}
